import { useId } from 'react';
import CountedField from './CountedField';
import LineListEditor from './LineListEditor';
import MaterialsRowsEditor from './MaterialsRowsEditor';
import { SERVICE_TEXT_LIMITS, formatDurationRange, type FieldErrors, type ServicePageDraft } from './servicePageDraft';

/**
 * Spec 081a — the "Service page" block of wizard step 1, in the mobile service page's order:
 * headline → warranty (days + details) → duration (read-only; edited in step 2) → description →
 * what's included → materials & details. English and Arabic side by side. All fields are optional
 * except the English description (it was required before this spec and still is).
 */
export interface ServicePageSectionProps {
  value: ServicePageDraft;
  onChange: (patch: Partial<ServicePageDraft>) => void;
  errors: FieldErrors;
  /** Computed from the step-2 pricing (flat duration or the variants' min–max); null when none is set. */
  duration: { min: number; max: number } | null;
}

export default function ServicePageSection({ value, onChange, errors, duration }: ServicePageSectionProps) {
  const titleId = useId();
  const daysId = useId();
  const L = SERVICE_TEXT_LIMITS;
  const daysError = errors.warrantyDays;
  const durationText = formatDurationRange(duration);

  return (
    <section className="svc-page" aria-labelledby={titleId} data-testid="service-page-section">
      <div className="partner-modal-section-title" id={titleId}>Service page</div>
      <p className="v2-hint">What customers read on this service's page in the app, in the same order. Arabic is optional; when it is empty the app shows the English text.</p>

      {/* 1. Headline */}
      <div className="svc-page__pair">
        <CountedField label="Headline (English)" lang="en" value={value.headline} maxLength={L.headline}
          placeholder="e.g. Full synthetic oil change in 45 minutes" error={errors.headline}
          onChange={(v) => onChange({ headline: v })} testId="service-headline" />
        <CountedField label="Headline (Arabic)" lang="ar" value={value.headlineAr} maxLength={L.headline}
          error={errors.headlineAr} onChange={(v) => onChange({ headlineAr: v })} testId="service-headline-ar" />
      </div>

      {/* 2. Warranty */}
      <div className="svc-page__group-title">Warranty</div>
      <div className={`svc-field svc-page__days${daysError ? ' svc-field--error' : ''}`}>
        <label htmlFor={daysId}>Warranty length (days)</label>
        <input
          id={daysId}
          type="number"
          inputMode="numeric"
          min={L.warrantyDaysMin}
          max={L.warrantyDaysMax}
          step={1}
          value={value.warrantyDays}
          placeholder="e.g. 180"
          aria-invalid={daysError ? true : undefined}
          aria-describedby={daysError ? `${daysId}-error` : `${daysId}-hint`}
          data-testid="service-warranty-days"
          onChange={(e) => onChange({ warrantyDays: e.target.value })}
        />
        <div className="svc-field__meta">
          {daysError
            ? <span id={`${daysId}-error`} className="svc-field__error" role="alert">{daysError}</span>
            : <span id={`${daysId}-hint`} className="svc-field__hint">Optional · {L.warrantyDaysMin}–{L.warrantyDaysMax} days · empty = no warranty</span>}
        </div>
      </div>
      <div className="svc-page__pair">
        <CountedField label="Warranty details (English)" lang="en" multiline rows={2} value={value.warrantyDescription}
          maxLength={L.warrantyDescription} placeholder="e.g. 6 months / 10,000 km" error={errors.warrantyDescription}
          onChange={(v) => onChange({ warrantyDescription: v })} testId="service-warranty-description" />
        <CountedField label="Warranty details (Arabic)" lang="ar" multiline rows={2} value={value.warrantyDescriptionAr}
          maxLength={L.warrantyDescription} error={errors.warrantyDescriptionAr}
          onChange={(v) => onChange({ warrantyDescriptionAr: v })} testId="service-warranty-description-ar" />
      </div>

      {/* 3. Duration — read-only here; edited per variant / flat price in step 2 */}
      <div className="svc-page__readonly" data-testid="service-duration">
        <span className="svc-page__readonly-label">Duration</span>
        <span className={`svc-page__readonly-value${durationText ? '' : ' svc-page__readonly-value--muted'}`}>
          {durationText ?? 'Set in Cars & Pricing'}
        </span>
      </div>

      {/* 4. Description */}
      <div className="svc-page__group-title">Description</div>
      <div className="svc-page__pair">
        <CountedField label="Description (English)" lang="en" multiline rows={4} required value={value.description}
          maxLength={L.description} placeholder="Describe the service: what you do and how." error={errors.description}
          onChange={(v) => onChange({ description: v })} testId="service-description" />
        <CountedField label="Description (Arabic)" lang="ar" multiline rows={4} value={value.descriptionAr}
          maxLength={L.description} error={errors.descriptionAr}
          onChange={(v) => onChange({ descriptionAr: v })} testId="service-description-ar" />
      </div>

      {/* 5. What's included */}
      <div className="svc-page__group-title">What's included</div>
      <p className="v2-hint">One item per line, up to {L.listItems}. Blank lines are dropped on save.</p>
      <div className="svc-page__pair">
        <LineListEditor label="What's included (English)" lang="en" items={value.whatsIncluded}
          onChange={(items) => onChange({ whatsIncluded: items })} maxItems={L.listItems} maxLength={L.listItem}
          errorKey="whatsIncluded" errors={errors} placeholder="e.g. Oil filter replacement" testId="service-whats-included" />
        <LineListEditor label="What's included (Arabic)" lang="ar" items={value.whatsIncludedAr}
          onChange={(items) => onChange({ whatsIncludedAr: items })} maxItems={L.listItems} maxLength={L.listItem}
          errorKey="whatsIncludedAr" errors={errors} placeholder="مثال: تغيير فلتر الزيت" testId="service-whats-included-ar" />
      </div>

      {/* 6. Materials & details */}
      <MaterialsRowsEditor rows={value.materialsDetails} onChange={(rows) => onChange({ materialsDetails: rows })} errors={errors} />
    </section>
  );
}
